import { useCallback } from 'react';

import { Icon } from 'components/Icon/Icon';
import { Pill } from 'components/Pill/Pill';

import { CheckboxPillsUl } from './CheckboxPills.styled';
import type { CheckboxPillOption } from './types/CheckboxPillOption';

type Props<TValue extends string> = {
  id?: string;
  name: string;
  values: TValue[];
  onChange: (value: TValue[]) => void;
  options: CheckboxPillOption<TValue>[];
  qaId?: string;
  size?: 'small' | 'large';
  maxDesktopWidth?: number;
};

export function CheckboxPills<TValue extends string>({
  id,
  name,
  values,
  onChange,
  options,
  qaId,
  size = 'large',
  maxDesktopWidth,
}: Props<TValue>) {
  const toggle = useCallback(
    (value: TValue) => {
      const newValues = [...values];
      const index = newValues.indexOf(value);

      if (index === -1) {
        newValues.push(value as TValue);
      } else {
        newValues.splice(index, 1);
      }

      onChange(newValues);
    },
    [onChange, values],
  );

  return (
    <CheckboxPillsUl
      id={id}
      data-qa-id={qaId}
      $maxDesktopWidth={maxDesktopWidth}
    >
      {options.map((option) => (
        <li key={option.value}>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label data-value={option.value}>
            <input
              type="checkbox"
              name={name}
              value={option.value}
              onChange={() => toggle(option.value)}
              style={{ display: 'none' }}
              checked={values.includes(option.value)}
            />

            <Pill size={size} suffix={<Icon name="close" size={16} />}>
              {option.label}
            </Pill>
          </label>
        </li>
      ))}
    </CheckboxPillsUl>
  );
}
